import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Gem, Star, Trophy, CheckCircle } from "lucide-react";

const plans = [
  {
    title: "Free",
    price: "₹0",
    icon: <Star size={36} className="text-white/80 drop-shadow-md" />,
    features: ["Basic Tracking", "Limited Insights", "Community Access"],
    cta: "Get Started",
    color: "from-gray-800 to-gray-700",
    border: "border-white/10",
  },
  {
    title: "Pro (Gold)",
    price: "₹299/mo",
    icon: <Trophy size={40} className="text-yellow-300 fill-yellow-400 drop-shadow-lg" />,
    features: [
      "Full Tracking Suite",
      "AI-Powered Insights",
      "Nutrition Planning",
      "Priority Support",
    ],
    cta: "Upgrade Now",
    color: "from-yellow-900/40 to-yellow-800/30",
    border: "border-yellow-400",
  },
  {
    title: "Ultimate (Diamond)",
    price: "₹499/mo",
    icon: <Gem size={40} className="text-cyan-300 fill-cyan-400 drop-shadow-lg" />,
    features: [
      "All Pro Features",
      "1-on-1 Coaching",
      "Early Access",
      "Exclusive Community",
    ],
    cta: "Go Diamond",
    color: "from-cyan-900/50 to-cyan-800/20",
    border: "border-cyan-400",
  },
];

export default function Pricing() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="min-h-screen bg-[#0e1a10] text-white px-4 py-20 font-manrope">
      {loading ? (
    <div className="w-2/4 h-12 mx-auto rounded-lg bg-[#1a2a1c] animate-pulse mb-10" />
  ) : (
      <motion.h1
        className="text-4xl sm:text-5xl font-extrabold text-green-400 text-center mb-16"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Choose Your Plan
      </motion.h1>
  )}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 perspective">
        {loading
          ? Array(3)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 h-[400px] animate-pulse shadow-lg transform transition"
                >
                  <div className="h-10 w-10 rounded-full bg-gray-700 mb-4" />
                  <div className="h-5 w-1/2 bg-gray-600 rounded mb-4" />
                  <div className="h-8 w-1/3 bg-gray-500 rounded mb-6" />
                  <div className="space-y-3">
                    <div className="h-4 w-full bg-gray-600 rounded" />
                    <div className="h-4 w-full bg-gray-600 rounded" />
                    <div className="h-4 w-3/4 bg-gray-600 rounded" />
                    <div className="h-4 w-2/3 bg-gray-600 rounded" />
                  </div>
                  <div className="mt-6 h-10 bg-gray-700 rounded-full w-full" />
                </div>
              ))
          : plans.map((plan, i) => (
              <motion.div
                key={plan.title}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2, duration: 0.5 }}
                viewport={{ once: true }}
                className={`
                  group bg-gradient-to-br ${plan.color} 
                  ${plan.border} border rounded-3xl 
                  shadow-2xl transform hover:-translate-y-3 hover:rotate-[0.5deg] 
                  transition duration-300 ease-in-out hover:shadow-green-400/20
                  p-8 flex flex-col items-center text-center
                `}
              >
                <div className="mb-4">{plan.icon}</div>
                <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
                <p className="text-3xl font-extrabold text-green-400 mb-6">{plan.price}</p>

                <ul className="text-gray-300 space-y-3 text-sm w-full">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 justify-center">
                      <CheckCircle size={16} className="text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  className="mt-8 w-full py-3 px-6 bg-green-500 hover:bg-green-400 text-black font-semibold rounded-full shadow-md transition"
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
      </div>
    </section>
  );
}
